import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography, Tabs, Tab } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAnalyticContext } from '../../../../hooks/analyticContext';
import { configureStackedBarChartForCost } from '../../../ChartConfiguration/ChartConfiguration';
import ClaimsPerformance from '../../../../Models/Claims/ClaimsPreview.model';
import TopTenMembers from '../../../../Models/Claims/TopTenMembers.model';
import FilterOptions from '../DetailFilterOption';
import { getFfCostGraph, getTopTenFfCostMembers, quarter, month } from '../AnalyticService';
import { ClaimAnalyticEnum } from '../../../../Enum/ClaimAnalyticEnum';
import { moneyFormatter } from '../../../../utils/moneyFormat';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

const FfCostDetail = () => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isMemberShow, setIsMemberShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [monthChartOptions, setMonthChartOptions] = useState({});
    const [graphSummary, setGraphSummary] = useState<ClaimsPerformance[]>([]);
    const [topTenMembers, setTopTenMembers] = useState<TopTenMembers[]>([]);
    const [selectedYear, setSelectedYear] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
    const chartHeight = 380;

    const {
        costSummaryHealthPlanIds,
        setCostSummaryHealthPlanIds,
        costSummaryYears,
        setCostSummaryYears,
        costSummaryMonths,
        setCostSummaryMonths,
        costSummaryTeam,
        setCostSummaryTeam,
        showCostSummaryInActiveTeams,
        setShowCostSummaryInActiveTeams,
    } = useAnalyticContext();

    const ffHealthPlanIds = costSummaryHealthPlanIds;
    const setFfHealthPlanIds = setCostSummaryHealthPlanIds;
    const ffYears = costSummaryYears;
    const setFfYears = setCostSummaryYears;
    const ffMonths = costSummaryMonths;
    const setFfMonths = setCostSummaryMonths;
    const ffTeam = costSummaryTeam;
    const setFfTeam = setCostSummaryTeam;
    const showFfInActiveTeams = showCostSummaryInActiveTeams;
    const setShowFfInActiveTeams = setShowCostSummaryInActiveTeams;


    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setMonthChartOptions(
                configureStackedBarChartForCost(
                    'Acute BH Cost',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    '',
                    history,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    const changeFilter = async () => {
        if (ffHealthPlanIds && ffHealthPlanIds.length > 0) {
            setIsGraphShow(false);
            setIsMemberShow(false);
            const data = await getFfCostGraph(
                ffHealthPlanIds,
                ffTeam,
                ffYears,
                ffMonths,
                showFfInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);

            const members = await getTopTenFfCostMembers(
                ffHealthPlanIds,
                ffTeam,
                ffYears,
                ffMonths,
                showFfInActiveTeams
            );
            setTopTenMembers(members);
            setIsMemberShow(true);
        }
    };

    useEffect(() => {
        if (!isFetched && ffHealthPlanIds && ffHealthPlanIds.length > 0) {
            changeFilter();
            setIsFetched(true);
        }
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [ffHealthPlanIds]);

    useEffect(() => {
        if (graphSummary && isPrepared) {

            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'FF Cost',
                    data: graphSummary.map((x) => ({
                        y: x.TotalCost,
                        total: x.MemberCount
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setMonthChartOptions(
                configureStackedBarChartForCost(
                    'FF Cost',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'FFCost',
                    history,
                    chartHeight
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
    }, [graphSummary, ffHealthPlanIds, isPrepared, history]);

    const columns: GridColDef[] = [
        {
            field: 'Rank',
            headerName: '#',
            maxWidth: 70,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'Cost',
            headerName: 'Cost',
            flex: 1,
            valueGetter: (value, row) => `${moneyFormatter.format(row.Cost)}`,
        },
    ];

    const navigateToGraph = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    return (
        <Paper elevation={5}>
            <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                <Grid size={12} paddingBottom={1}>
                    <span onClick={navigateToGraph}>
                        <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                    </span>
                    <Typography display={'inline-block'} variant="h6" color={'primary'}>
                        Acute BH Costs
                    </Typography>
                </Grid>
                <FilterOptions
                    healthPlanIds={ffHealthPlanIds}
                    setHealthPlanIds={setFfHealthPlanIds}
                    years={ffYears}
                    setYears={setFfYears}
                    months={ffMonths}
                    setMonths={setFfMonths}
                    team={ffTeam}
                    setTeam={setFfTeam}
                    showInActiveTeams={showFfInActiveTeams}
                    setShowInActiveTeams={setShowFfInActiveTeams}
                    onFetch={changeFilter}
                    claimType={ClaimAnalyticEnum.FfCost}
                />
                <Grid size={5} marginTop={1} sx={{ position: 'relative' }}>
                    {!isGraphShow ? (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    ) : null}
                    <Grid container>
                        <Grid size={12} marginTop={2} style={{ backgroundColor: '#F5F5F5' }} paddingY={1} paddingLeft={2} paddingRight={2}>
                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                Acute BH Costs
                            </Typography>
                        </Grid>
                    </Grid>
                    <HighchartsReact highcharts={Highcharts} options={monthChartOptions} />
                </Grid>
                <Grid size={1}></Grid>
                <Grid size={6} marginTop={1}>
                    <Grid container>
                        <Grid size={12} marginTop={2} style={{ backgroundColor: '#F5F5F5' }} paddingY={1} paddingLeft={2} paddingRight={2}>
                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                Top 10 Members
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                        <Grid size={12} marginTop={2} minHeight="40vh" sx={{ position: 'relative' }}>
                            {!isGraphShow || !isMemberShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <Tabs
                                        value={selectedYear}
                                        onChange={(e, newValue) => setSelectedYear(newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        {topTenMembers?.map((detail, index) => (
                                            <Tab key={index} label={detail.Year === '0' ? "Baseline" : detail.Year} />
                                        ))}
                                    </Tabs>
                                    <Grid spacing={2}>
                                        {topTenMembers?.map((detail, index) => {
                                            const rowsWithRank = detail.Members.map((m, i) => ({
                                                ...m,
                                                Rank: i + 1,
                                            }));
                                            return (
                                                selectedYear === index && (
                                                    <Grid container key={`detail-${index}`}>
                                                        <Grid size={12} sx={{ height: '55vh', width: '100%' }}>
                                                            <div style={{ width: '100%', height: '55vh', overflow: 'auto' }}>
                                                                <DataGrid
                                                                    rows={rowsWithRank}
                                                                    columns={columns}
                                                                    paginationModel={paginationModel}
                                                                    onPaginationModelChange={setPaginationModel}
                                                                    pageSizeOptions={[5]}
                                                                    getRowId={(row) => row.Id + row.AhcccsId}
                                                                    getRowHeight={() => 45}
                                                                    autoHeight={false}
                                                                    sx={{
                                                                        height: '100%',
                                                                        [`& .MuiDataGrid-cell`]: { py: 1 },
                                                                    }}
                                                                />
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                )
                                            )
                                        })}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default FfCostDetail;